import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';
import {
  sanitizeSupplyOrderPayload,
  sanitizeSupplyOrdersFromExternalOrderPayload,
} from '../stock-compras/supply-orders/utils/supplyOrdersUtils';

const supplyOrderKeys = {
  all: ['supplyOrders'],
  lists: () => [...supplyOrderKeys.all, 'list'],
  list: (filters = {}) => [...supplyOrderKeys.lists(), filters],
  details: () => [...supplyOrderKeys.all, 'detail'],
  detail: (id) => [...supplyOrderKeys.details(), id],
};

const buildListParams = (filters = {}) => {
  const params = {};
  const allowedKeys = [
    'source_type',
    'status',
    'uuid_provider_legal_entity',
    'uuid_source_warehouse',
    'uuid_destination_warehouse',
    'uuid_external_order',
    'date_from',
    'date_to',
    'required_from',
    'required_to',
    'query',
  ];

  allowedKeys.forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== '') {
      params[key] = value;
    }
  });

  return params;
};

const invalidateSupplyOrderRelatedQueries = (queryClient, order) => {
  queryClient.invalidateQueries({ queryKey: supplyOrderKeys.lists() });

  if (order?.uuid) {
    queryClient.invalidateQueries({ queryKey: supplyOrderKeys.detail(order.uuid) });
  }

  if (order?.uuid_external_order) {
    queryClient.invalidateQueries({ queryKey: ['externalOrders'] });
  }

  queryClient.invalidateQueries({ queryKey: ['articleStocks'] });
  queryClient.invalidateQueries({ queryKey: ['stockMovements'] });
};

const downloadPdfFromResponse = (data) => {
  const { pdf_base64, download_name } = data || {};

  if (!pdf_base64) return;

  const byteCharacters = atob(pdf_base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: 'application/pdf' });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', download_name || 'orden_abastecimiento.pdf');
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const useSupplyOrders = (filters = {}, options = {}) => {
  const params = buildListParams(filters);

  return useQuery({
    queryKey: supplyOrderKeys.list(params),
    queryFn: async () => {
      const response = await apiClient.get('supply_orders', { params });
      return response.data;
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useSupplyOrder = (id, options = {}) => {
  return useQuery({
    queryKey: supplyOrderKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`supply_orders/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 30 * 1000,
    ...options,
  });
};

export const useCreateSupplyOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const response = await apiClient.post(
        'supply_orders',
        sanitizeSupplyOrderPayload(payload)
      );
      return response.data;
    },
    onSuccess: (data) => {
      invalidateSupplyOrderRelatedQueries(queryClient, data);
    },
  });
};

export const useUpdateSupplyOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(
        `supply_orders/${id}`,
        sanitizeSupplyOrderPayload(data)
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      invalidateSupplyOrderRelatedQueries(queryClient, data || { uuid: variables.id });
    },
  });
};

export const useIssueSupplyOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.post(`supply_orders/${id}/issue`);
      return response.data;
    },
    onSuccess: (data) => {
      invalidateSupplyOrderRelatedQueries(queryClient, data);
    },
  });
};

export const useMarkSupplyOrderSent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, recipient_email }) => {
      const response = await apiClient.post(`supply_orders/${id}/mark-sent`, {
        recipient_email: recipient_email || undefined,
      });
      return response.data;
    },
    onSuccess: (data) => {
      invalidateSupplyOrderRelatedQueries(queryClient, data);
    },
  });
};

export const useAcknowledgeSupplyOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.post(`supply_orders/${id}/acknowledge`);
      return response.data;
    },
    onSuccess: (data) => {
      invalidateSupplyOrderRelatedQueries(queryClient, data);
    },
  });
};

export const useReceiveSupplyOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.post(`supply_orders/${id}/receive`, data);
      return response.data;
    },
    onSuccess: (data) => {
      invalidateSupplyOrderRelatedQueries(queryClient, data);
    },
  });
};

export const useCancelSupplyOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }) => {
      const response = await apiClient.post(`supply_orders/${id}/cancel`, { reason });
      return response.data;
    },
    onSuccess: (data) => {
      invalidateSupplyOrderRelatedQueries(queryClient, data);
    },
  });
};

export const useCloseSupplyOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }) => {
      const response = await apiClient.post(`supply_orders/${id}/close`, { reason });
      return response.data;
    },
    onSuccess: (data) => {
      invalidateSupplyOrderRelatedQueries(queryClient, data);
    },
  });
};

export const usePrintSupplyOrder = () => {
  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.get(`supply_orders/${id}/print`);
      downloadPdfFromResponse(response.data);
      return response.data;
    },
  });
};

export const useCreateSupplyOrdersFromExternalOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ externalOrderId, payload }) => {
      const response = await apiClient.post(
        `supply_orders/from-external-order/${externalOrderId}`,
        sanitizeSupplyOrdersFromExternalOrderPayload(payload)
      );
      return response.data;
    },
    onSuccess: (orders, variables) => {
      queryClient.invalidateQueries({ queryKey: supplyOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['externalOrders'] });
      queryClient.invalidateQueries({
        queryKey: ['externalOrders', 'detail', variables.externalOrderId],
      });

      if (Array.isArray(orders)) {
        orders.forEach((order) => {
          if (order?.uuid) {
            queryClient.invalidateQueries({ queryKey: supplyOrderKeys.detail(order.uuid) });
          }
        });
      }
    },
  });
};

export default supplyOrderKeys;
